import {useEffect, useRef, useState} from "react";
import Spinner from "../../../Spinner";
import filterResponse from "../../../../filterResponse";
import {INSPECTOR_LOCK_NOTE, INSPECTOR_LOCK_REASON, REVIEW_BADGES, REVIEW_LABELS, SETTABLE_LABELS, SETTABLE_STATUSES, displayStatus, lockedForInspector} from "./reviewStatus";

// The per-entry QA verdict, in the two forms it takes (DEV-3040).
//
// An inspector (PM or QA, here through Inspect) gets the radios and the comment box. The
// contributor gets the same verdict read-only — and nothing at all while it is still pending,
// because "we have not looked at this yet" is not something to put in front of them.
export default function ReviewStatusCell({entry, editEntry, inspector}) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [comment, setComment] = useState(entry.reviewComment || "");

	// What the server last confirmed, so an unchanged comment is not saved again on every blur.
	const savedComment = useRef(entry.reviewComment || "");

	const status = displayStatus(entry);

	// Approved means it has gone to the client and been paid for, so the verdict is settled. Greyed
	// rather than hidden: the row should read as finished, not as missing its controls.
	const locked = lockedForInspector(entry);

	// The row polls itself while QA is running and replaces the entry wholesale, and another
	// inspector may have written a verdict in the meantime. Follow the server unless this box is
	// being typed in — losing what someone is halfway through writing would be worse.
	const commentRef = useRef(null);
	useEffect(() => {
		const fresh = entry.reviewComment || "";
		if (fresh !== savedComment.current) {
			savedComment.current = fresh;
			if (document.activeElement !== commentRef.current) {
				setComment(fresh);
			}
		}
	}, [entry.reviewComment]);

	async function saveReview(reviewStatus, reviewComment) {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(process.env.REACT_APP_ENDPOINT + '/crowdEntries/' + entry.id + '/review', {
				method: "PUT",
				headers: {
					"Content-Type": "application/json"
				},
				credentials: "include",
				body: JSON.stringify({reviewStatus, reviewComment})
			});
			await filterResponse(response);

			if (response.status !== 200) {
				// The reasons the server refuses a verdict are ones the inspector has to read —
				// the entry is already approved, or the columns are not migrated yet. A generic
				// "Not saved" next to a control that looks live reads as a glitch to retry.
				const refusal = await response.json().catch(() => null);
				throw Object.assign(new Error("Wrong status code."), {refusal: refusal?.error});
			}

			const {entry: fresh} = await response.json();
			savedComment.current = fresh.reviewComment || "";
			await editEntry(fresh.id, fresh);
		}
		catch (e) {
			// The job is no longer available to this contributor, so the page is already being
			// replaced with the server's message and there is nothing to report here. Matched on
			// the code rather than the error class, so this file does not depend on that feature
			// having landed.
			if (e.code !== "JOB_UNAVAILABLE") {
				console.error("Failed to save the review of entry " + entry.id, e);
				setError(e.refusal || "Not saved");
			}
		}

		setLoading(false);
	}

	if (!inspector) {
		if (status === "pending") {
			return <td />;
		}

		return (
			<td className={"text-nowrap"}>
				<span className={"badge " + REVIEW_BADGES[status]}>{REVIEW_LABELS[status]}</span>
				{entry.reviewComment &&
					<div className={"small text-body-secondary text-wrap mt-1"} style={{maxWidth: "16rem"}}>{entry.reviewComment}</div>
				}
			</td>
		);
	}

	return (
		<td style={{minWidth: "13rem"}}>
			<div className={"d-flex flex-column gap-1"}>
				<div className={"d-flex gap-2 align-items-center text-nowrap"}>
					{SETTABLE_STATUSES.map(value => {
						return (
							<div className={"form-check m-0"} key={value}>
								<input
									className={"form-check-input"}
									type={"radio"}
									name={`reviewStatus-${entry.id}`}
									id={`reviewStatus-${entry.id}-${value}`}
									checked={entry.reviewStatus === value}
									disabled={loading || locked}
									title={locked ? INSPECTOR_LOCK_REASON : undefined}
									onChange={() => saveReview(value, comment)}
								/>
								<label className={"form-check-label small"} htmlFor={`reviewStatus-${entry.id}-${value}`} title={locked ? INSPECTOR_LOCK_REASON : undefined}>
									{SETTABLE_LABELS[value]}
								</label>
							</div>
						);
					})}
					{loading && <Spinner small={true} />}
				</div>

				{/* Both are worth showing even to the person setting the verdict: "Approved" says it
				    has gone to the client, "Resubmitted" says the contributor has changed it since. */}
				{(status === "approved" || status === "resubmitted") &&
					<span className={"badge align-self-start " + REVIEW_BADGES[status]}>{REVIEW_LABELS[status]}</span>
				}

				{locked &&
					<span className={"small text-body-secondary"} title={INSPECTOR_LOCK_REASON}>{INSPECTOR_LOCK_NOTE}</span>
				}

				<textarea
					ref={commentRef}
					className={"form-control form-control-sm"}
					rows={1}
					placeholder={"QA comment (optional)"}
					value={comment}
					disabled={loading || locked}
					title={locked ? INSPECTOR_LOCK_REASON : undefined}
					onChange={evt => setComment(evt.target.value)}
					onBlur={() => {
						if (comment !== savedComment.current) {
							// No status: the verdict stays whatever it is, only the comment changes.
							saveReview(undefined, comment);
						}
					}}
				/>

				{error && <span className={"small text-danger"}>{error}</span>}
			</div>
		</td>
	);
}
